import { Koi } from '@ksyos/koi';
import { SpecialismCode } from '@common/enums';
import type { ISchemaContainer } from './schemaContainer';
import { Profession } from '../enums/profession';
import {
    agbCodeSchema,
    identifierRegistrationBigBackendSchema,
    identifierRegistrationBigFrontendSchema,
} from './schemas/identifierDeclarationSchemas';
import { specialismCodeValidator } from './utils/accountFieldsCustomValidationFunctions';
import {
    identifierDeclarationOrSupervisorRequired,
    identifierDeclarationRequired,
    identifierRegistrationBigAllowed,
    supervisorRequired,
} from './utils/accountFieldsRequirementsByProfession';

export interface IEmploymentPractitionerRegistrationCodesValidated {
    hasDeclarableQualification?: boolean | null;
    identifierDeclaration?: string | null;
    identifierDeclarationPractitionerId: string | null;
    identifierRegistrationBig?: string | null;
    profession: Profession | null;
    specialismCode?: string | null;
}

const employmentPractitionerRegistrationCodesBase = Koi.object({
    hasDeclarableQualification: Koi.when('identifierDeclaration', {
        is: Koi.string().regex(/^84\d*$/),
        otherwise: Koi.any(),
        then: Koi.boolean(),
    }),
    identifierDeclaration: Koi.when('profession', {
        is: Koi.valid(...identifierDeclarationRequired),
        otherwise: Koi.when('profession', {
            is: Koi.valid(...identifierDeclarationOrSupervisorRequired),
            otherwise: Koi.any(),
            then: Koi.when('identifierDeclarationPractitionerId', {
                is: Koi.string(),
                otherwise: agbCodeSchema,
                then: Koi.any(),
            }),
        }),
        then: agbCodeSchema,
    }),
    identifierDeclarationPractitionerId: Koi.when('profession', {
        is: Koi.valid(...supervisorRequired),
        otherwise: Koi.when('profession', {
            is: Koi.valid(...identifierDeclarationOrSupervisorRequired),
            otherwise: Koi.any(),
            then: Koi.when('identifierDeclaration', {
                is: Koi.string(),
                otherwise: Koi.string().uuid(),
                then: Koi.any(),
            }),
        }),
        then: Koi.string().uuid(),
    }),
    profession: Koi.koi().enum(Profession),
    specialismCode: Koi.when('profession', {
        is: Profession.medicalSpecialist,
        otherwise: specialismCodeValidator,
        then: Koi.koi().enum(SpecialismCode),
    }).optional(),
});

// eslint-disable-next-line @stylistic/max-len
export const employmentPractitionerRegistrationCodesBackendSchema: ISchemaContainer<IEmploymentPractitionerRegistrationCodesValidated> =
    {
        schema: employmentPractitionerRegistrationCodesBase
            .concat(
                Koi.object().keys({
                    identifierRegistrationBig: Koi.when('profession', {
                        is: Koi.valid(...identifierRegistrationBigAllowed),
                        otherwise: identifierRegistrationBigBackendSchema.allow(null),
                        then: identifierRegistrationBigBackendSchema,
                    }),
                })
            )
            .options({ presence: 'required' }),
    };

// eslint-disable-next-line @stylistic/max-len
export const employmentPractitionerRegistrationCodesFrontendSchema: ISchemaContainer<IEmploymentPractitionerRegistrationCodesValidated> =
    {
        schema: employmentPractitionerRegistrationCodesBase
            .concat(
                Koi.object().keys({
                    identifierRegistrationBig: Koi.when('profession', {
                        is: Koi.valid(...identifierRegistrationBigAllowed),
                        otherwise: identifierRegistrationBigFrontendSchema.allow(null),
                        then: identifierRegistrationBigFrontendSchema,
                    }),
                })
            )
            .options({ presence: 'required' }),
    };
